import React, { useRef } from 'react';
import { 
  Button, 
  ButtonProps, 
  IconButton, 
  useTheme, 
  useMediaQuery, 
  Tooltip,
  SxProps,
  Theme
} from '@mui/material';

export interface ResponsiveButtonProps extends Omit<ButtonProps, 'startIcon' | 'endIcon'> {
  /**
   * Ícone do botão. Em mobile, pode ser mostrado sozinho se mobileIconOnly=true
   */
  icon?: React.ReactNode;
  /**
   * Texto do botão. Em mobile, pode ser ocultado se mobileIconOnly=true
   */
  children?: React.ReactNode;
  /**
   * Se true, em mobile mostra apenas o ícone. Em desktop sempre mostra texto + ícone
   * @default true
   */
  mobileIconOnly?: boolean;
  /**
   * Texto do tooltip quando o botão está apenas com ícone em mobile
   */
  tooltipTitle?: string;
  /**
   * Texto curto para mobile quando mobileIconOnly=false mas o texto original é muito longo
   * Exemplo: "Exportar PDF" -> "PDF" em mobile
   */
  mobileText?: string;
  /**
   * Prevenir double-click em dispositivos móveis
   * @default true
   */
  preventDoubleClick?: boolean;
  /**
   * Breakpoint para considerar como mobile
   * @default 'md'
   */
  mobileBreakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

/**
 * Componente Button responsivo que se adapta automaticamente para mobile e desktop.
 * 
 * Em mobile:
 * - Se mobileIconOnly=true: mostra apenas ícone com tooltip
 * - Se mobileIconOnly=false: mostra texto (usa mobileText se fornecido, senão children)
 * 
 * Em desktop:
 * - Sempre mostra ícone + texto completo
 * 
 * Segue as melhores práticas da indústria:
 * - Tamanho mínimo de toque de 44x44px em mobile
 * - Prevenção de double-tap
 * - Tooltips informativos
 * - Textos curtos em mobile
 */
const ResponsiveButton: React.FC<ResponsiveButtonProps> = ({
  icon,
  children,
  mobileIconOnly = true,
  tooltipTitle,
  mobileText,
  preventDoubleClick = true,
  mobileBreakpoint = 'md',
  onClick,
  sx,
  ...buttonProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
  const lastClickTime = useRef<number>(0);
  const isProcessing = useRef<boolean>(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!onClick) return;

    // Prevenir double-click em mobile
    if (preventDoubleClick && isMobile) {
      const now = Date.now();
      const timeSinceLastClick = now - lastClickTime.current;
      
      // Se clicou muito rápido (menos de 500ms) ou está processando, ignorar
      if (timeSinceLastClick < 500 || isProcessing.current) {
        event.stopPropagation();
        return;
      }
      
      lastClickTime.current = now;
      isProcessing.current = true;
      
      // Resetar flag após um tempo
      setTimeout(() => {
        isProcessing.current = false;
      }, 500);
    }
    
    onClick(event);
  };

  // Determinar a cor do ícone baseada no variant
  const getIconColor = (): string => {
    if (buttonProps.color === 'error') return 'error.main';
    if (buttonProps.color === 'warning') return 'warning.main';
    if (buttonProps.color === 'success') return 'success.main';
    if (buttonProps.color === 'info') return 'info.main';
    if (buttonProps.variant === 'contained') return 'inherit';
    return 'primary.main'; // Default para outlined e outros
  };

  // Em mobile, mostrar apenas ícone se mobileIconOnly=true
  if (isMobile && mobileIconOnly && icon) {
    const iconButton = (
      <IconButton
        {...(buttonProps as Record<string, unknown>)}
        onClick={handleClick}
        onTouchStart={(e) => {
          // Prevenir double-tap em touch devices
          if (preventDoubleClick) {
            e.stopPropagation();
            const target = e.currentTarget as HTMLElement;
            if (target.dataset.touched === 'true') {
              e.preventDefault();
              return;
            }
            target.dataset.touched = 'true';
            setTimeout(() => {
              delete target.dataset.touched;
            }, 500);
          }
        }}
        sx={{
          color: getIconColor(),
          minWidth: 44, // Tamanho mínimo de toque recomendado
          minHeight: 44,
          '&:hover': {
            backgroundColor: buttonProps.variant === 'contained' 
              ? 'action.hover' 
              : `${getIconColor()}15`, // 15 = ~8% opacity
          },
          ...sx,
        } as SxProps<Theme>}
      >
        {icon}
      </IconButton>
    );

    // Se houver tooltip, envolver o IconButton
    if (tooltipTitle) {
      return (
        <Tooltip title={tooltipTitle} arrow>
          {iconButton}
        </Tooltip>
      );
    }

    return iconButton;
  }

  // Em desktop ou quando mobileIconOnly=false, mostrar botão completo
  const displayText = isMobile && mobileText ? mobileText : children;
  const displayIcon = icon;

  const fullButton = (
    <Button
      {...buttonProps}
      startIcon={displayIcon}
      onClick={handleClick}
      sx={{
        // Garantir tamanho mínimo de toque em mobile
        minHeight: isMobile ? 44 : undefined,
        minWidth: isMobile ? 44 : undefined,
        // Texto mais compacto em mobile
        fontSize: isMobile ? '0.875rem' : undefined,
        padding: isMobile ? '8px 12px' : undefined,
        ...sx,
      } as SxProps<Theme>}
    >
      {displayText}
    </Button>
  );

  // Se estiver em mobile e tiver tooltip mas não está em modo iconOnly, mostrar tooltip
  if (isMobile && tooltipTitle && !mobileIconOnly) {
    return (
      <Tooltip title={tooltipTitle} arrow>
        {fullButton}
      </Tooltip>
    );
  }

  return fullButton;
};

export default ResponsiveButton;

